import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sparkles, Users, ImageIcon as ImageLucideIcon, TrendingUp, Camera, Edit } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { useToast } from '@/hooks/use-toast';
import EmojiPicker, { twemojiUrl } from '@/components/ui/emoji-picker';
import { JoinedCommunitiesCarousel } from '@/components/communities/JoinedCommunitiesCarousel';

interface Community {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  color?: string;
  gradient?: string;
  banner_url?: string;
  featured_image_url?: string;
  is_fomo_official: boolean;
  is_admin_curated?: boolean;
  member_count: number;
  post_count: number;
}

// Floating action button and dialog to create a community (premium/admin only)
function CreateCommunityFab({ inline = false }: { inline?: boolean }) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [name, setName] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [description, setDescription] = React.useState('');
  // Tag & emote
  const [tag, setTag] = React.useState('');
  const [emote, setEmote] = React.useState<string>('');
  const avatarRef = React.useRef<HTMLInputElement | null>(null);
  const bannerRef = React.useRef<HTMLInputElement | null>(null);
  const featuredRef = React.useRef<HTMLInputElement | null>(null);
  const [avatarFile, setAvatarFile] = React.useState<File | null>(null);
  const [bannerFile, setBannerFile] = React.useState<File | null>(null);
  const [featuredFile, setFeaturedFile] = React.useState<File | null>(null);

  const tagValid = /^([A-Za-z0-9]{3,5})$/.test(tag);
  const tagUpper = tag.toUpperCase();

  const uploadIfAny = async (file: File | null, id: string, key: string) => {
    if (!file) return undefined as string | undefined;
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `${id}/${key}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('community-images').upload(path, file, {
      cacheControl: '3600', upsert: true, contentType: file.type || 'image/jpeg'
    });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from('community-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const onCreate = async () => {
    try {
      if (!user?.id) {
        toast({ title: 'Sign in required', description: 'You must be signed in to create a community', variant: 'destructive' });
        return;
      }
      if (!name || !displayName) {
        toast({ title: 'Missing fields', description: 'Please provide a handle and a display name', variant: 'destructive' });
        return;
      }
      if (!tagValid) {
        toast({ title: 'Invalid tag', description: 'Tag must be 3–5 letters or numbers (A–Z, 0–9).', variant: 'destructive' });
        return;
      }
      setSubmitting(true);

      // Create a temporary id to build storage paths
      const tempId = crypto.randomUUID();
      const [avatarUrl, bannerUrl, featuredUrl] = await Promise.all([
        uploadIfAny(avatarFile, tempId, 'avatar'),
        uploadIfAny(bannerFile, tempId, 'banner'),
        uploadIfAny(featuredFile, tempId, 'featured')
      ]);

      // Insert (RLS allows premium/admin for non-official)
      const insertPayload: any = {
        id: tempId,
        name: name.trim().toLowerCase(),
        display_name: displayName.trim(),
        description: description.trim() || null,
        is_fomo_official: false,
        is_auto_generated: false,
        is_admin_curated: !!(avatarUrl || bannerUrl || featuredUrl),
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
        featured_image_url: featuredUrl,
        short_tag: tagUpper,
        emote: emote || null,
      };

      const { error } = await supabase.from('communities').insert(insertPayload);
      if (error) throw error;

      toast({ title: 'Community created', description: `#${name} is live!` });
      setOpen(false);
      navigate(`/community/${name.trim().toLowerCase()}`);
    } catch (e: any) {
      toast({ title: 'Failed to create community', description: e.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={inline ? 'outline' : undefined}
          size={inline ? 'sm' : undefined}
          className={inline ? '' : 'fixed bottom-6 right-6 shadow-lg'}
        >
          + Create Community
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create a Community</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-sm text-muted-foreground">Handle</label>
            <input className="col-span-2 input input-bordered w-full bg-background border rounded px-3 py-2"
              placeholder="e.g. art"
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^a-z0-9_-]/g, '').toLowerCase())}
            />
          </div>
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-sm text-muted-foreground">Display Name</label>
            <input className="col-span-2 input input-bordered w-full bg-background border rounded px-3 py-2"
              placeholder="Art"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-sm text-muted-foreground">Description</label>
            <textarea className="col-span-2 input input-bordered w-full bg-background border rounded px-3 py-2"
              rows={3}
              placeholder="What is this community about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Tag & Emote */}
          <div className="rounded-lg border p-3 bg-muted/10">
            <div className="grid grid-cols-3 items-center gap-3">
              <label className="text-sm text-muted-foreground">Tag (3–5 letters or numbers)</label>
              <div className="col-span-2 flex items-center gap-2">
                <input
                  className="input input-bordered bg-background border rounded px-3 py-2 tracking-widest text-sm w-40"
                  placeholder="A1RT"
                  value={tagUpper}
                  onChange={(e) => setTag(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0,5))}
                />
                <div className={cn("inline-flex items-center gap-2 rounded-md border px-2 py-1", tagValid ? "border-border" : "border-destructive/50")}
                  title="Tag preview"
                >
                  {emote ? (
                    <img src={twemojiUrl(emote)} alt={emote} className="h-4 w-4" style={{ imageRendering: 'pixelated' as any }} />
                  ) : (
                    <span className="text-muted-foreground text-xs">🙂</span>
                  )}
                  <span className="font-mono tracking-widest text-sm">{tagUpper || 'TAG'}</span>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <div className="text-sm text-muted-foreground mb-2">Pick an emote</div>
              <EmojiPicker selectedEmoji={emote} onEmojiSelect={setEmote} />
            </div>
          </div>

          {/* Media uploads */}
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-sm text-muted-foreground">Avatar</label>
            <div className="col-span-2 flex items-center gap-2">
              <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={(e) => setAvatarFile(e.target.files?.[0] || null)} />
              <Button variant="outline" size="sm" onClick={() => avatarRef.current?.click()}>Upload</Button>
              <span className="text-xs text-muted-foreground truncate">{avatarFile?.name}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-sm text-muted-foreground">Banner</label>
            <div className="col-span-2 flex items-center gap-2">
              <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => setBannerFile(e.target.files?.[0] || null)} />
              <Button variant="outline" size="sm" onClick={() => bannerRef.current?.click()}>Upload</Button>
              <span className="text-xs text-muted-foreground truncate">{bannerFile?.name}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="text-sm text-muted-foreground">Featured</label>
            <div className="col-span-2 flex items-center gap-2">
              <input ref={featuredRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFeaturedFile(e.target.files?.[0] || null)} />
              <Button variant="outline" size="sm" onClick={() => featuredRef.current?.click()}>Upload</Button>
              <span className="text-xs text-muted-foreground truncate">{featuredFile?.name}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={onCreate} disabled={submitting || !name || !displayName || !tagValid}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CommunitiesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { isAdmin, isPremium } = useUserRoles();
  const canCreateCommunity = isAdmin || isPremium;
  
  // Fetch all communities
  const { data: communities, isLoading } = useQuery({
    queryKey: ['all-communities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .order('is_fomo_official', { ascending: false })
        .order('post_count', { ascending: false });

      if (error) throw error;
      return data as Community[];
    }
  });

  // Fetch recent posts from communities for featured images
  const { data: communityPosts } = useQuery({
    queryKey: ['community-featured-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('loop_communities')
        .select(`
          community_id,
          loop:loop_id (id, image_url, thumbnail_url)
        `)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      // Group by community and take the most recent post image
      const postsByCommId = new Map<string, {imageUrl: string}>();
      data?.forEach(item => {
        // Type assertion to handle the nested loop properties
        const loop = item.loop as any;
        if (!postsByCommId.has(item.community_id) && loop?.image_url) {
          postsByCommId.set(item.community_id, {
            imageUrl: loop.thumbnail_url || loop.image_url
          });
        }
      });
      
      return postsByCommId;
    },
    enabled: !!communities
  });

  // Function to get featured image for a community
  const getFeaturedImage = (community: Community) => {
    // First try the explicitly set featured image
    if (community.featured_image_url) {
      return community.featured_image_url;
    }
    
    // Then try a recent post image
    if (communityPosts && communityPosts.has(community.id)) {
      return communityPosts.get(community.id).imageUrl;
    }
    
    // Fallback - generate gradient background
    return null;
  };

  // Sort communities for display priority
  const sorted = React.useMemo(() => {
    if (!communities) return [] as Community[];
    return [...communities].sort((a, b) => {
      // FOMO official first
      if (a.is_fomo_official && !b.is_fomo_official) return -1;
      if (!a.is_fomo_official && b.is_fomo_official) return 1;
      // Admin curated next
      if ((a.is_admin_curated ? 1 : 0) !== (b.is_admin_curated ? 1 : 0)) {
        return (b.is_admin_curated ? 1 : 0) - (a.is_admin_curated ? 1 : 0);
      }
      // Then by post_count desc
      return (b.post_count || 0) - (a.post_count || 0);
    });
  }, [communities]);

  // Simple uniform grid - all cards same size
  const getTileClasses = (idx: number, c: Community) => {
    return 'col-span-1 row-span-1';
  };

  // Admin capabilities remain available where applicable
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="container mx-auto max-w-6xl">
          <h1 className="text-3xl font-bold mb-6">Discover Communities</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square rounded-md animate-pulse bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Discover Communities</h1>
          {canCreateCommunity && <CreateCommunityFab inline />}
        </div>
        
        {/* Joined Communities Carousel */}
        {user && <JoinedCommunitiesCarousel userId={user.id} />}
        
        {/* Discovery Section Header */}
        <h2 className="text-xl font-bold text-white mb-4">All Communities</h2>
        
        {/* Simple uniform grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {sorted.map((community, idx) => (
            <div key={community.id} className="w-full">
              <VisualCommunityCard 
                community={community}
                featuredImage={getFeaturedImage(community)}
                onClick={() => navigate(`/community/${community.name}`)}
                isAdmin={isAdmin}
                sizeHint="small"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Inline button added beside title; no floating button */}
    </div>
  );
}

// Visual community card styled like Tumblr
function VisualCommunityCard({ 
  community, 
  featuredImage, 
  onClick,
  isAdmin = false,
  sizeHint = 'small'
}: { 
  community: Community; 
  featuredImage: string | null; 
  onClick: () => void;
  isAdmin?: boolean;
  sizeHint?: 'small' | 'medium' | 'large' | 'hero';
}) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = React.useState(false);
  
  // Handle admin image upload
  const handleImageUpdate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isAdmin) return;
    
    setIsUpdating(true);
    
    // In a real implementation, you'd open a file dialog and upload the image
    toast({
      title: "Feature Coming Soon",
      description: "Admin image upload functionality will be added soon!",
    });
    
    setIsUpdating(false);
  };
  
  const aspect = sizeHint === 'hero' ? 'aspect-[2/1]' : 
                 sizeHint === 'large' ? 'aspect-[3/2]' : 
                 sizeHint === 'medium' ? 'aspect-[4/3]' : 
                 'aspect-square';

  return (
    <div 
      onClick={onClick}
      className={cn(
        'relative overflow-hidden rounded-md cursor-pointer transition-transform hover:scale-[1.02] group',
        aspect,
        'bg-gradient-to-br',
        !featuredImage && community.gradient ? '' : 'from-gray-800 to-gray-900'
      )}
      style={{
        background: !featuredImage && community.gradient ? community.gradient : undefined,
      }}
    >
      {/* Featured image */}
      {featuredImage && (
        <div className="absolute inset-0 w-full h-full bg-black/30">
          <img 
            src={featuredImage} 
            alt={community.display_name}
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
          />
        </div>
      )}
      
      {/* Community info overlay */}
      <div className="absolute inset-0 p-4 flex flex-col justify-end">
        {/* Gradient overlay at bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent pointer-events-none" />
        
        {community.is_fomo_official && (
          <div className={cn(
            "absolute top-3 right-3 text-primary z-10",
            sizeHint === 'hero' && "top-4 right-4"
          )}>
            <Sparkles className={cn("w-5 h-5", sizeHint === 'hero' && "w-6 h-6")} />
          </div>
        )}
        
        {/* Content container with responsive sizing */}
        <div className="relative z-10">
          <h3 className={cn(
            "font-bold text-white drop-shadow-lg leading-tight mb-2",
            sizeHint === 'hero' ? "text-3xl lg:text-4xl" :
            sizeHint === 'large' ? "text-2xl lg:text-3xl" :
            sizeHint === 'medium' ? "text-xl lg:text-2xl" :
            "text-lg lg:text-xl"
          )}>
            #{community.name}
          </h3>
          
          <p className={cn(
            "text-white/80 drop-shadow-md mb-3 leading-snug",
            sizeHint === 'hero' ? "text-lg lg:text-xl" :
            sizeHint === 'large' ? "text-base lg:text-lg" :
            "text-sm lg:text-base"
          )}>
            {community.description || community.display_name}
          </p>
          
          <div className="flex items-center gap-4 text-white/70">
            <div className="flex items-center gap-1">
              <Users className={cn("w-4 h-4", sizeHint === 'hero' && "w-5 h-5")} />
              <span className={cn(
                "text-sm font-medium",
                sizeHint === 'hero' && "text-base"
              )}>
                {(community.member_count || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className={cn("w-4 h-4", sizeHint === 'hero' && "w-5 h-5")} />
              <span className={cn(
                "text-sm font-medium",
                sizeHint === 'hero' && "text-base"
              )}>
                {(community.post_count || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        
        {/* Admin update button */}
        {isAdmin && (
          <button
            className="absolute top-3 left-3 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            onClick={handleImageUpdate}
            disabled={isUpdating}
            title="Update community image"
          >
            <Camera className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// Legacy card component (kept for reference)
function CommunityCard({ 
  community, 
  featuredImage, 
  onClick 
}: { 
  community: Community; 
  featuredImage: string | null; 
  onClick: () => void;
}) {
  return (
    <Card className="cursor-pointer transition-all hover:shadow-md border border-white/10 bg-black/30" onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center overflow-hidden">
            {community.icon ? (
              <img 
                src={community.icon} 
                alt={community.display_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-lg font-bold text-white">
                {community.display_name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-white truncate">
                {community.display_name}
              </h3>
              {community.is_fomo_official && (
                <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
              )}
            </div>
            <p className="text-sm text-white/60">#{community.name}</p>
          </div>
        </div>
        
        {community.description && (
          <p className="text-white/80 text-sm mb-4 line-clamp-2">
            {community.description}
          </p>
        )}
        
        <div className="flex items-center justify-between text-sm text-white/60">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{(community.member_count || 0).toLocaleString()} members</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            <span>{(community.post_count || 0).toLocaleString()} posts</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}